import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import type { ProductMeta } from '../../data/productRegistry';

interface ProductTileProps {
  product: ProductMeta;
  name: string;
  tagline: string;
  description: string;
  cta: string;
  deploymentLabels?: string[];
}

const ProductTile: React.FC<ProductTileProps> = ({ product, name, tagline, description, cta, deploymentLabels }) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const Icon = product.icon;

  const onMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--x', `${e.clientX - r.left}px`);
    el.style.setProperty('--y', `${e.clientY - r.top}px`);
  };

  return (
    <Link
      ref={ref}
      to={`/products/${product.slug}`}
      onMouseMove={onMove}
      className="glow-ring group relative flex flex-col h-full overflow-hidden rounded-3xl glass-card p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_rgba(34,211,238,0.45)]"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: 'radial-gradient(420px circle at var(--x, 50%) var(--y, 0%), rgba(34,211,238,0.12), transparent 45%)' }}
      />
      <div className={`pointer-events-none absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gradient-to-br ${product.accent} opacity-20 blur-3xl transition-opacity duration-500 group-hover:opacity-40`} />

      <div className="relative flex items-start justify-between mb-6">
        <div className={`relative p-3 rounded-2xl bg-gradient-to-br ${product.accent} shadow-lg`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        {product.badge && (
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-cyan-200 border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
            {product.badge}
          </span>
        )}
      </div>

      <h3 className="relative text-xl font-bold text-white mb-1.5">{name}</h3>
      <p className="relative text-sm font-medium text-cyan-200/80 mb-4">{tagline}</p>
      <p className="relative text-sm text-slate-400 leading-relaxed mb-6 line-clamp-3">{description}</p>

      {deploymentLabels && deploymentLabels.length > 0 && (
        <div className="relative flex flex-wrap gap-1.5 mb-6">
          {deploymentLabels.map((d) => (
            <span key={d} className="text-[11px] font-medium text-slate-300 border border-white/10 bg-white/[0.03] px-2 py-1 rounded-md">
              {d}
            </span>
          ))}
        </div>
      )}

      <div className="relative mt-auto flex items-center justify-between pt-5 border-t border-white/[0.06]">
        <span className="text-sm font-semibold text-white">{cta}</span>
        <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-cyan-300 transition-all duration-300 group-hover:bg-gradient-to-br group-hover:from-cyan-300 group-hover:to-emerald-300 group-hover:text-slate-950 group-hover:border-transparent">
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
        </span>
      </div>
    </Link>
  );
};

export default ProductTile;
